import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { Eye, GripVertical, Pencil, RotateCcw, SlidersHorizontal } from 'lucide-react'
import { apiGet, apiMutate, usePermissions } from '../admin/primitives.js'
import { useSaveBar } from '../shell/editing.js'
import { useMenuContextuel, type GroupeMenu } from '../shell/menu-contextuel.js'
import {
  VISIBILITY_LABELS, getPageLayout, modeOf, reconcileLayout, violatedConstraint,
  visibleForPhase,
  type PageLayoutDef, type WidgetCapabilities, type WidgetPlacement,
} from './registry.js'
import { WidgetConfigModal } from './WidgetConfigModal.js'
import { ecranEtroit, gestesNatifs } from './menu-tuiles.js'
import { ORDRE_VISIBILITE, POIGNEES_TUILE, sauf } from './menu-tuiles-modele.js'

/**
 * LAYOUT GRID — rendu + mode édition (drag, resize, masquage, restauration).
 *
 * Grille LOGIQUE 12 colonnes, hauteurs en unités de ligne — jamais de
 * pixels persistés. Le drag/resize est au pointeur (aucune dépendance),
 * fluide via transform, et la grille réagit en direct (les autres widgets
 * glissent à leur place — FLIP CSS sur grid-row/column).
 *
 * Mobile (< 900 px) : les widgets s'empilent dans l'ordre vertical du
 * layout — on ne « réduit » jamais mécaniquement une grille 4 colonnes ;
 * l'édition est desktop (le modèle device_class est prêt côté serveur).
 */

const COLS = 12
export const ROW_H = 84
const GAP = 14

/**
 * Le menu d'une tuile ne s'arme ni sur ses poignées, ni sur un champ, ni sur
 * un texte sélectionné, ni dans un tiroir ou une confirmation que son widget
 * monte en portail ; au doigt, ni sur un menu ou un lien imbriqué.
 */
const HORS_MENU_TUILE = gestesNatifs(POIGNEES_TUILE)

interface DragState {
  id: string
  kind: 'move' | 'resize'
  startX: number
  startY: number
  origin: WidgetPlacement
  dx: number
  dy: number
}

function collides(a: WidgetPlacement, b: WidgetPlacement): boolean {
  if (a.hidden || b.hidden) return false
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
}

/** Après un déplacement : le widget bougé garde sa place, les autres
 *  descendent jusqu'à la première ligne libre (déterministe, sans pixels). */
function resolveCollisions(widgets: WidgetPlacement[], movedId: string): WidgetPlacement[] {
  const out = widgets.map((w) => ({ ...w }))
  const moved = out.find((w) => w.id === movedId)
  if (!moved) return out
  const others = out.filter((w) => w.id !== movedId && !w.hidden)
    .sort((a, b) => a.y - b.y || a.x - b.x)
  for (const other of others) {
    let guard = 0
    while (collides(moved, other) && guard < 200) { other.y += 1; guard += 1 }
    // Les suivants se re-vérifient contre celui-ci aussi.
    for (const later of others) {
      if (later === other) continue
      let g2 = 0
      while (collides(other, later) && g2 < 200) { later.y += 1; g2 += 1 }
    }
  }
  return out
}

/**
 * AUTO-COMPACTION — gravité vers le haut : chaque widget remonte au plus
 * haut possible SANS collision, en préservant l'ordre relatif (parcours en
 * (y, x) des positions courantes — l'intention utilisateur d'abord, pas de
 * page qui danse). Le widget épinglé (en cours de drag) ne bouge pas.
 */
function compactUp(widgets: WidgetPlacement[], pinnedId: string | null = null): WidgetPlacement[] {
  const out = widgets.map((w) => ({ ...w }))
  const order = out.filter((w) => !w.hidden)
    .sort((a, b) => a.y - b.y || a.x - b.x)
  const placed: WidgetPlacement[] = []
  for (const w of order) {
    if (w.id !== pinnedId) {
      while (w.y > 0) {
        const test = { ...w, y: w.y - 1 }
        if (placed.some((p) => collides(test, p))) break
        w.y = test.y
      }
    }
    placed.push(w)
  }
  return out
}

export function useLayoutState(pageKey: string): {
  def: PageLayoutDef | null
  widgets: WidgetPlacement[]
  /** Accepte une mise à jour fonctionnelle : le menu d'une tuile agit après son ouverture. */
  setWidgets: Dispatch<SetStateAction<WidgetPlacement[]>>
  editing: boolean
  setEditing: (v: boolean) => void
  resetToDefault: () => void
  loaded: boolean
  capabilities: Record<string, WidgetCapabilities>
  canEdit: boolean
} {
  const def = getPageLayout(pageKey)
  const [widgets, setWidgets] = useState<WidgetPlacement[]>([])
  const [snapshot, setSnapshot] = useState<WidgetPlacement[] | null>(null)
  const [editing, setEditing] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [capabilities, setCapabilities] = useState<Record<string, WidgetCapabilities>>({})
  const [canEdit, setCanEdit] = useState(false)

  /*
   * LA DISPOSITION EST UN DÉFAUT DE PRODUIT, PAS UNE PRÉFÉRENCE.
   *
   * On lit `/page-layout/:page` — le défaut GLOBAL, commun à tous. La route
   * personnelle `/me/page-layout` reste en place, inutilisée par cette page :
   * elle sera le maillon « surcharge personnelle » le jour où on décidera
   * d'en avoir une. La résolution vit côté serveur, en un seul endroit.
   *
   * Les CAPACITÉS viennent du serveur elles aussi : le navigateur ne devine
   * pas ce qu'une tuile sait faire, et c'est la même table qui valide
   * l'écriture.
   */
  useEffect(() => {
    let vivant = true
    apiGet<{
      widgets: WidgetPlacement[] | null
      capabilities?: Record<string, WidgetCapabilities>
      can_edit?: boolean
    }>(`/api/v1/page-layout/${pageKey}`)
      .then((r) => {
        if (!vivant || !def) return
        const w = reconcileLayout(def, r.widgets)
        setWidgets(w); setSnapshot(w.map((x) => ({ ...x })))
        setCapabilities(r.capabilities ?? {})
        setCanEdit(r.can_edit === true)
        setLoaded(true)
      })
      .catch(() => {
        if (!vivant || !def) return
        const w = reconcileLayout(def, null)
        setWidgets(w); setSnapshot(w.map((x) => ({ ...x }))); setLoaded(true)
      })
    return () => { vivant = false }
  }, [pageKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // La Floating Save Bar est présente PENDANT TOUT le mode édition (pas
  // seulement après le premier changement — c'était le bug perçu) ; le
  // compteur reflète les vrais écarts.
  const changed = snapshot !== null && JSON.stringify(widgets) !== JSON.stringify(snapshot)
  const dirty = editing

  /*
   * Une combinaison que l'écran ne sait pas rendre BLOQUE l'enregistrement.
   *
   * Le serveur la refuserait de toute façon, mais avec un code technique et
   * plusieurs écrans trop tard. La barre dit ici la même chose que la modale,
   * dans les mêmes mots, avant que l'utilisateur ait fermé quoi que ce soit.
   */
  const invalide = useMemo(() => {
    for (const w of widgets) {
      const c = violatedConstraint(capabilities[w.id], w.config ?? {})
      if (c) {
        const nom = def?.widgets.find((d) => d.id === w.id)?.label ?? w.id
        return `${nom} — ${c.message}`
      }
    }
    return null
  }, [widgets, capabilities, def])

  useSaveBar({
    dirty,
    ...(changed ? { count: 1 } : {}),
    invalid: invalide,
    saveLabel: 'Enregistrer la disposition',
    onSave: async () => {
      const r = await apiMutate<{ ok?: boolean; error?: string; message?: string }>(
        'PUT', `/api/v1/page-layout/${pageKey}`, { widgets })
      if (r.status !== 200) return r.data.message ?? 'Disposition refusée par le serveur.'
      setSnapshot(widgets.map((x) => ({ ...x })))
      setEditing(false)
      return true
    },
    onCancel: () => {
      if (snapshot) setWidgets(snapshot.map((x) => ({ ...x })))
      setEditing(false)
    },
    successMessage: 'Disposition enregistrée — c’est désormais le défaut de la page pour tout le monde.',
  })

  const resetToDefault = useCallback(() => {
    if (!def) return
    setWidgets(def.defaultLayout.map((w) => ({ ...w })))
  }, [def])

  return { def, widgets, setWidgets, editing, setEditing, resetToDefault, loaded, capabilities, canEdit }
}

export function LayoutGrid({ pageKey, state, phase, absentes }: {
  pageKey: string
  state: ReturnType<typeof useLayoutState>
  /**
   * Tuiles qu'un fait de l'INSTANCE retire de la page hors édition — un
   * emplacement éteint dans les Réglages, des montants que ce compte ne lit
   * pas (la Caisse de la nuit, 15/10/2026). Une tuile vide laisserait un trou ;
   * la disposition enregistrée, elle, n'est pas touchée.
   */
  absentes?: readonly string[]
  /**
   * Phase de la soirée, calculée PAR LE SERVEUR (nuit commerciale, coupure
   * 08:00). C'est elle qui décide de la visibilité conditionnelle — jamais
   * un `Date.now()` dispersé dans React.
   */
  phase?: string | null
}) {
  const { def, widgets, setWidgets, editing, setEditing, resetToDefault, capabilities } = state
  const permissions = usePermissions()
  const menu = useMenuContextuel()
  const gridRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<DragState | null>(null)
  const [configuring, setConfiguring] = useState<string | null>(null)
  void pageKey

  const cellW = useCallback((): number => {
    const el = gridRef.current
    if (!el) return 100
    return (el.clientWidth - GAP * (COLS - 1)) / COLS
  }, [])

  const onPointerDown = (e: React.PointerEvent, id: string, kind: 'move' | 'resize') => {
    // Le bouton principal seulement, comme SectionOrder : un clic droit sur
    // une poignée lançait un glisser, et le relâcher recompactait le
    // brouillon en silence.
    if (e.button !== 0) return
    if (!editing) return
    const origin = widgets.find((w) => w.id === id)
    if (!origin) return
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId)
    setDrag({ id, kind, startX: e.clientX, startY: e.clientY, origin: { ...origin }, dx: 0, dy: 0 })
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return
    setDrag({ ...drag, dx: e.clientX - drag.startX, dy: e.clientY - drag.startY })
  }

  const onPointerUp = () => {
    if (!drag || !def) return
    const cw = cellW()
    const dCols = Math.round(drag.dx / (cw + GAP))
    const dRows = Math.round(drag.dy / (ROW_H + GAP))
    const defW = def.widgets.find((w) => w.id === drag.id)
    const next = widgets.map((w) => {
      if (w.id !== drag.id) return w
      if (drag.kind === 'move') {
        const x = Math.max(0, Math.min(COLS - w.w, drag.origin.x + dCols))
        const y = Math.max(0, drag.origin.y + dRows)
        return { ...w, x, y }
      }
      const minW = defW?.minW ?? 2
      const minH = defW?.minH ?? 2
      const maxH = defW?.maxH ?? 40
      const width = Math.max(minW, Math.min(COLS - w.x, drag.origin.w + dCols))
      const h = Math.max(minH, Math.min(maxH, drag.origin.h + dRows))
      return { ...w, w: width, h }
    })
    setWidgets(compactUp(resolveCollisions(next, drag.id), drag.id))
    setDrag(null)
  }

  /**
   * Applique un réglage venu de la modale (ou du menu de la tuile) au
   * BROUILLON de la page. Mise à jour fonctionnelle : le menu est construit
   * à son ouverture, il ne doit pas réécrire un brouillon périmé.
   */
  const patchWidget = (id: string, patch: Partial<WidgetPlacement>) => {
    setWidgets((avant) => {
      const next = avant.map((w) => (w.id === id ? { ...w, ...patch } : w))
      // Un changement de visibilité peut laisser un trou : on recompacte, mais
      // seulement l'affichage — l'ORDRE canonique reste celui qu'on a choisi.
      return patch.visibility !== undefined ? compactUp(next) : next
    })
  }

  /*
   * MENU CLIC DROIT D'UNE TUILE (24/10/2026) — pour qui peut modifier la
   * page (`can_edit`, soit soiree.manage côté serveur), et pour lui seul.
   *
   * Chaque entrée reprend un geste de la page : le bouton « Modifier la
   * page », le bouton réglages de la tuile (la MÊME fenêtre, ouverte par
   * `setConfiguring`), le choix de visibilité de cette fenêtre, et
   * « Disposition par défaut ». Tout reste un brouillon que la barre
   * d'enregistrement confirme, comme avant ; le menu entre d'abord en
   * édition. Sous 900 px, la barre de la tuile n'existe pas (layout.css) :
   * ni « Configurer » ni « Visibilité ».
   */
  const actionsTuile = (p: WidgetPlacement): GroupeMenu[] => {
    const groupes: GroupeMenu[] = []
    if (!editing) {
      groupes.push({ actions: [{ id: 'modifier', libelle: 'Modifier la page', icone: <Pencil size={15} />, agir: () => setEditing(true) }] })
    }
    if (!ecranEtroit()) {
      const courant = modeOf(p)
      groupes.push({ actions: [
        { id: 'configurer', libelle: 'Configurer cette tuile…', icone: <SlidersHorizontal size={15} />,
          agir: () => { setEditing(true); setConfiguring(p.id) } },
        { id: 'visibilite', libelle: 'Visibilité pour tout le monde', icone: <Eye size={15} />,
          sousMenu: ORDRE_VISIBILITE.map((m) => ({
            id: `vis-${m}`, libelle: VISIBILITY_LABELS[m], coche: m === courant,
            ...(m === courant ? { indisponible: 'Réglage actuel' } : {}),
            agir: () => { setEditing(true); patchWidget(p.id, { visibility: m }) },
          })) },
      ] })
    }
    if (editing) {
      groupes.push({ actions: [{ id: 'defaut', libelle: 'Disposition par défaut', icone: <RotateCcw size={15} />, agir: resetToDefault }] })
    }
    return groupes
  }

  const visible = useMemo(() => {
    if (!def) return []
    const byId = new Map(def.widgets.map((w) => [w.id, w]))
    const visibles = widgets
      .filter((p) => byId.has(p.id))
      // RBAC AU RENDU : jamais un widget sans permission, quel que soit le JSON.
      .filter((p) => {
        const perm = byId.get(p.id)!.requiredPermission
        return !perm || permissions.permissions.includes(perm)
      })
      /*
       * EN ÉDITION, ON NE FILTRE RIEN.
       *
       * Une tuile masquée qui disparaîtrait de l'éditeur serait impossible à
       * réactiver ; une tuile conditionnelle qui s'évaporerait sur une soirée
       * à venir laisserait croire qu'elle a été perdue. Elles restent donc
       * là, atténuées et étiquetées.
       */
      .filter((p) => editing || visibleForPhase(modeOf(p), phase))
      .filter((p) => editing || !(absentes ?? []).includes(p.id))
    // Masquer des blocs laisse des trous : leurs voisins gardaient leur ligne
    // d'origine et la page se retrouvait pleine de vide. On recompacte la
    // liste RÉELLEMENT affichée — sans toucher à la disposition enregistrée,
    // qui reste celle que l'utilisateur a choisie.
    return editing ? visibles : compactUp(visibles)
  }, [def, widgets, permissions, editing, phase, absentes])

  if (!def) return null

  return (
    <div
      ref={gridRef}
      className={`lg-grid lc-stagger ${editing ? 'lg-grid--editing' : ''}`}
      style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)`, gridAutoRows: `${ROW_H}px`, gap: GAP }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {visible.map((p) => {
        const w = def.widgets.find((x) => x.id === p.id)!
        const dragging = drag?.id === p.id
        const mode = modeOf(p)
        // « Conditionnelle » = elle a une règle, et cette règle l'écarte de la
        // phase actuelle. On la garde à l'écran en édition, en le disant.
        const conditionnelle = mode !== 'HIDDEN' && !visibleForPhase(mode, phase)
        const transform = dragging && drag
          ? drag.kind === 'move'
            ? `translate(${drag.dx}px, ${drag.dy}px)`
            : undefined
          : undefined
        return (
          <section
            key={p.id}
            className={`lg-widget ${mode === 'HIDDEN' ? 'lg-widget--hidden' : ''} ${conditionnelle ? 'lg-widget--conditional' : ''} ${dragging ? 'lg-widget--dragging' : ''}`}
            style={{
              gridColumn: `${p.x + 1} / span ${p.w}`,
              gridRow: `${p.y + 1} / span ${p.h}`,
              ...(transform ? { transform } : {}),
              ...(dragging && drag?.kind === 'resize'
                ? { minWidth: 0, width: `calc(100% + ${drag.dx}px)`, height: `calc(100% + ${drag.dy}px)` }
                : {}),
            }}
            aria-label={w.label}
            {...(state.canEdit ? sauf(menu(() => actionsTuile(p), w.label), HORS_MENU_TUILE) : {})}
          >
            {editing ? (
              <header className="lg-widget__bar">
                <button
                  type="button" className="lg-widget__grip" aria-label={`Déplacer ${w.label}`}
                  onPointerDown={(e) => onPointerDown(e, p.id, 'move')}
                >
                  <GripVertical size={14} />
                  <span>{w.label}</span>
                </button>
                {/* La règle en vigueur, lisible d'un coup d'œil — en édition
                    seulement. En consultation, elle n'apprendrait rien. */}
                <span className={`lg-widget__rule lg-widget__rule--${mode.toLowerCase().replace('_', '-')}`}>
                  {VISIBILITY_LABELS[mode]}
                </span>
                {/* Déclencheur DISTINCT de la poignée de déplacement : une
                    icône de réglages, discrète, qui ouvre la configuration de
                    CETTE tuile. */}
                <button type="button" className="lg-widget__tool"
                  aria-label={`Configurer ${w.label}`} title="Configurer cette tuile"
                  onClick={() => setConfiguring(p.id)}>
                  <SlidersHorizontal size={14} aria-hidden="true" />
                </button>
              </header>
            ) : null}
            <div className="lg-widget__body">
              {editing && mode === 'HIDDEN'
                ? <p className="lc-hint lg-widget__ghost">Masquée — absente de la page.</p>
                : editing && conditionnelle
                  ? <p className="lc-hint lg-widget__ghost">
                      {mode === 'FROM_START'
                        ? 'Visible à partir du lancement de la soirée.'
                        : 'Visible avant la soirée seulement.'}
                    </p>
                  : <w.Component {...(p.config !== undefined ? { config: p.config } : {})} />}
            </div>
            {editing ? (
              <button
                type="button" className="lg-widget__resize" aria-label={`Redimensionner ${w.label}`}
                onPointerDown={(e) => onPointerDown(e, p.id, 'resize')}
              />
            ) : null}
          </section>
        )
      })}
      <WidgetConfigModal
        open={configuring !== null}
        onClose={() => setConfiguring(null)}
        def={configuring ? def.widgets.find((x) => x.id === configuring) ?? null : null}
        placement={configuring ? widgets.find((x) => x.id === configuring) ?? null : null}
        capabilities={configuring ? capabilities[configuring] : undefined}
        onChange={(patch) => { if (configuring) patchWidget(configuring, patch) }}
      />
    </div>
  )
}

export function LayoutEditControls({ state }: { state: ReturnType<typeof useLayoutState> }) {
  const { editing, setEditing, resetToDefault } = state
  if (!editing) {
    return (
      <button type="button" className="lc-btn" onClick={() => setEditing(true)}>
        Modifier la page
      </button>
    )
  }
  return (
    <div className="lg-editbar">
      <span className="lc-hint">Glissez, redimensionnez, masquez — puis enregistrez.</span>
      <button type="button" className="lc-btn" onClick={resetToDefault}>
        <RotateCcw size={14} aria-hidden="true" /> Disposition par défaut
      </button>
    </div>
  )
}
